import { useState, useEffect, useRef } from 'react';
import { getOne, update } from './../../../../common/v4/BackendProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { EditPage, EditPagePanel, EditPageSelect, EditPageTextField, EditPageCheckbox } from './../../../../common/EditPage';

const CarrierSmppAccountsEditClient = () => {
    const { client_id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [metadata, setMetadata] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [smppStatus, setSmppStatus] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const { carrier_id } = useParams();
    const refreshIntervalRef = useRef(null);

    useEffect(() => {
        getOne(`/admin/carriers/carriers/${carrier_id}/client_smppaccounts/`, client_id).then(response => {
            setData(response.data.data);
            setMetadata(response.data.metadata);
        });
    }, []);

    // Function to fetch SMPP status
    const fetchSmppStatus = async () => {
        if (!data.username) return;
        
        setLoadingStatus(true);
        try {
            const response = await fetch(`/api/v2/admin/smpp/status/${data.username}`);
            const result = await response.json();
            if (result.result === 'OK') {
                setSmppStatus(result.data.connections || []);
            }
        } catch (error) {
            console.error('Error fetching SMPP status:', error);
            toast.error('Failed to fetch SMPP status');
        } finally {
            setLoadingStatus(false);
        }
    };

    // Fetch SMPP status when username changes and set up auto-refresh
    useEffect(() => {
        if (data.username) {
            // Initial fetch
            fetchSmppStatus();
            
            // Set up auto-refresh every 60 seconds
            refreshIntervalRef.current = setInterval(() => {
                fetchSmppStatus();
            }, 60000); // 60 seconds
            
            // Cleanup function to clear interval when component unmounts or username changes
            return () => {
                if (refreshIntervalRef.current) {
                    clearInterval(refreshIntervalRef.current);
                    refreshIntervalRef.current = null;
                }
            };
        } else {
            // Clear interval if no username
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        }
    }, [data.username]);

    const handleSave = () => {
        update(`/admin/carriers/carriers/${carrier_id}/client_smppaccounts/`, client_id, {data: data}).then(response => {
            toast.success("Carrier updated successfully", {});
            navigate(`/carriers/carriers/${carrier_id}/smppaccounts`);
        }).catch(error => {
            toast.error("Error updating carrier: " + error.message, {});
        });
    }

    const handleCancel = () => {
        navigate(`/carriers/carriers/${carrier_id}/smppaccounts`);
    }

    const onChange = (id, value) => {
        setData({...data, [id]: value});
    }
    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } >
                <EditPagePanel>
                    <EditPageSelect label="Rate Profile" id="product_id" onChange={ onChange } value={ data.product_id } options={ metadata?.product_id } />
                    <EditPageTextField label="Username" id="username" onChange={ onChange } value={ data.username } />
                    <EditPageTextField label="Password" id="password" onChange={ onChange } value={ data.password } />
                    <EditPageSelect label="Bind Mode" id="bind_mode" onChange={ onChange } value={ data.bind_mode } options={ metadata?.bind_mode } />
                    <EditPageSelect label="Coding" id="coding" onChange={ onChange } value={ data.coding } options={ metadata?.coding } />
                    <EditPageCheckbox label="Use SSL" id="use_ssl" onChange={ onChange } value={ data.use_ssl } />
                    <EditPageCheckbox label="Enabled" id="status" onChange={ onChange } value={ data.status } />
                </EditPagePanel>
                
                {/* SMPP Status Table */}
                {data.username && (
                    <EditPagePanel>
                        <div>
                            <h4>SMPP Connection Status</h4>
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th>Server Name</th>
                                            <th>Active Connections</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingStatus ? (
                                            <tr>
                                                <td colSpan="2" className="text-center">
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    Loading SMPP status...
                                                </td>
                                            </tr>
                                        ) : smppStatus.length === 0 ? (
                                            <tr>
                                                <td colSpan="2" className="text-center text-muted">
                                                    <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '12px' }}>
                                                        ●
                                                    </span>
                                                    No active connections found
                                                </td>
                                            </tr>
                                        ) : (
                                            smppStatus.map((server, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <strong>{server.server_name}</strong>
                                                    </td>
                                                    <td>
                                                        {server.connections && server.connections.length > 0 ? (
                                                            <ul className="list-unstyled mb-0">
                                                                {server.connections.map((connection, connIndex) => (
                                                                    <li key={connIndex} className="mb-1">
                                                                        <span className="badge" style={{ backgroundColor: '#198754', color: '#198754', marginRight: '12px' }}>
                                                                            ●
                                                                        </span>
                                                                        <code>{connection}</code>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <span className="text-muted">
                                                                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#dc3545', marginRight: '12px' }}>
                                                                    ●
                                                                </span>
                                                                No active connections
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-2">
                                <small className="text-muted">
                                    <i className="fas fa-sync-alt me-1"></i>
                                    Auto-refreshing every 60 seconds
                                </small>
                            </div>
                        </div>
                    </EditPagePanel>
                )}
            </EditPage>
        </>
    )
};

export default CarrierSmppAccountsEditClient;